import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { DndContext, useDraggable, useDroppable, type DragEndEvent } from '@dnd-kit/core';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CircleSlash,
  GripVertical,
  Hand,
  Info,
  Loader2,
  Lock,
  X,
} from 'lucide-react';
import { useContexto } from '@/hooks/useContexto';
import { useSessao } from '@/hooks/useSessao';
import { useParamUrl } from '@/hooks/useUrlEstado';
import { useCompensacao, useNegativos, useOrigens, type Perna } from '@/hooks/negativos';
import type { NegativoItem, OrigemCandidata } from '@/lib/tipos';
import { cn, numero, percentual, plural } from '@/lib/utils';
import { AbasMovimentos } from '@/componentes/layout/AbasMovimentos';
import { TransferenciaArrastar } from '@/componentes/movimentacao/TransferenciaArrastar';
import { DrawerProduto } from '@/componentes/produto/DrawerProduto';
import { ImagemProduto } from '@/componentes/produto/ImagemProduto';
import { Botao } from '@/componentes/ui/botao';
import { Aviso, EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { CabecalhoPagina, Cartao, Esqueleto, Pilula } from '@/componentes/ui/primitivos';

type Filtro = '' | 'resolviveis' | 'sem-origem';

function chaveItem(item: NegativoItem): string {
  return `${item.localizador}:${item.produto}`;
}

/**
 * Tratativa de saldos negativos.
 *
 * Saldo negativo é fisicamente impossível: o sistema deu baixa de algo que
 * não estava na posição. A correção é sempre transferir de uma posição que
 * tem o SKU para a que está negativa — a mesma regra que a operação já usa.
 * Nada de "ajuste" novo.
 */
export function Negativos(): JSX.Element {
  const { centro } = useContexto();
  const { temPermissao } = useSessao();
  const podeMover = temPermissao('estoque.mover');

  const [filtroUrl, definirFiltro] = useParamUrl('filtro');
  const filtro = (['resolviveis', 'sem-origem'].includes(filtroUrl) ? filtroUrl : '') as Filtro;
  const [selecionadoUrl, definirSelecionadoUrl] = useParamUrl('negativo');

  const negativos = useNegativos(centro);
  const itens = React.useMemo(() => negativos.data?.itens ?? [], [negativos.data]);

  const filtrados = React.useMemo(
    () =>
      itens.filter((item) =>
        filtro === 'resolviveis' ? item.resolvivel : filtro === 'sem-origem' ? !item.resolvivel : true,
      ),
    [itens, filtro],
  );

  // O link pode vir do mapa (só o localizador) ou da própria tela (localizador:produto).
  const selecionado = React.useMemo(() => {
    if (selecionadoUrl === '') return filtrados[0] ?? null;
    const porChave = itens.find((item) => chaveItem(item) === selecionadoUrl);
    if (porChave) return porChave;
    const localizador = Number(selecionadoUrl.split(':')[0]);
    return itens.find((item) => item.localizador === localizador) ?? filtrados[0] ?? null;
  }, [selecionadoUrl, itens, filtrados]);

  const resolviveis = itens.filter((item) => item.resolvivel).length;

  return (
    <div className="space-y-4 p-3 sm:p-5">
      <CabecalhoPagina
        icone={<AlertTriangle className="h-5 w-5" />}
        sobrelinha="Movimentos"
        titulo="Saldos negativos"
        descricao="Saldo negativo é fisicamente impossível: o sistema deu baixa de algo que não estava na posição. Compensar = transferir de uma posição que tem o SKU para a que está negativa."
        acoes={
          itens.length > 0 ? (
            <>
              <Pilula tom="erro">{numero(itens.length)} itens</Pilula>
              <Pilula tom="ok">
                {numero(resolviveis)} resolvíveis ·{' '}
                {percentual(itens.length > 0 ? (resolviveis / itens.length) * 100 : 0)}
              </Pilula>
            </>
          ) : null
        }
      />

      <AbasMovimentos ativa="negativos" totalNegativos={negativos.data?.total} />

      {!podeMover ? (
        <Aviso
          titulo="Você não tem permissão para movimentar estoque"
          detalhe="A lista abaixo é somente leitura. Compensar exige a permissão estoque.mover."
        />
      ) : null}

      {negativos.isLoading || centro === null ? (
        <div className="grid gap-3 lg:grid-cols-2">
          <Esqueleto className="h-80" />
          <Esqueleto className="h-80" />
        </div>
      ) : negativos.isError ? (
        <EstadoErro erro={negativos.error} aoTentarDeNovo={() => void negativos.refetch()} />
      ) : itens.length === 0 ? (
        <Cartao>
          <EstadoVazio
            titulo="Nenhum saldo negativo neste centro"
            detalhe="Nada a tratar por aqui."
            icone={<Check className="h-8 w-8" />}
          />
        </Cartao>
      ) : (
        <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <ListaNegativos
            itens={filtrados}
            filtro={filtro}
            aoFiltrar={(novo) => definirFiltro(novo === '' ? null : novo)}
            selecionado={selecionado}
            aoSelecionar={(item) => definirSelecionadoUrl(chaveItem(item))}
          />
          {selecionado ? (
            <PainelCompensacao
              key={chaveItem(selecionado)}
              item={selecionado}
              centro={centro}
              podeMover={podeMover}
            />
          ) : (
            <Cartao>
              <EstadoVazio titulo="Escolha um negativo à esquerda" />
            </Cartao>
          )}
        </div>
      )}
    </div>
  );
}

function ListaNegativos({
  itens,
  filtro,
  aoFiltrar,
  selecionado,
  aoSelecionar,
}: {
  itens: readonly NegativoItem[];
  filtro: Filtro;
  aoFiltrar: (filtro: Filtro) => void;
  selecionado: NegativoItem | null;
  aoSelecionar: (item: NegativoItem) => void;
}): JSX.Element {
  const opcoes: { valor: Filtro; rotulo: string }[] = [
    { valor: '', rotulo: 'Todos' },
    { valor: 'resolviveis', rotulo: 'Resolvíveis' },
    { valor: 'sem-origem', rotulo: 'Sem origem no centro' },
  ];

  return (
    <Cartao className="flex max-h-[70vh] flex-col overflow-hidden">
      <div className="flex flex-wrap gap-1.5 border-b border-borda bg-superficie2 p-2">
        {opcoes.map((opcao) => (
          <button
            key={opcao.valor}
            type="button"
            aria-pressed={filtro === opcao.valor}
            onClick={() => aoFiltrar(opcao.valor)}
            className={cn(
              'min-h-[36px] rounded-full border px-3.5 text-xs font-semibold transition-all',
              filtro === opcao.valor
                ? 'gradiente-marca border-transparent text-white shadow-xs'
                : 'border-borda bg-superficie hover:border-bordaForte hover:bg-superficie2',
            )}
          >
            {opcao.rotulo}
          </button>
        ))}
      </div>

      {itens.length === 0 ? (
        <EstadoVazio titulo="Nenhum item com esse filtro" />
      ) : (
        <ul className="min-h-0 flex-1 divide-y divide-borda overflow-y-auto">
          {itens.map((item) => {
            const ativo = selecionado !== null && chaveItem(selecionado) === chaveItem(item);
            return (
              <li key={chaveItem(item)}>
                <button
                  type="button"
                  onClick={() => aoSelecionar(item)}
                  aria-current={ativo}
                  className={cn(
                    'flex w-full items-start gap-3 px-3.5 py-3 text-left transition-colors hover:bg-superficie2',
                    ativo &&
                      'bg-primaria-suave shadow-[inset_3px_0_0_rgb(var(--cor-primaria))] hover:bg-primaria-suave',
                  )}
                >
                  <ImagemProduto sku={item.produto} tamanho={40} alt="" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-xs text-textoFraco">
                      {item.corredor} · {item.endereco}
                      <span className="ml-1 text-textoSuave">· LOC {item.localizador}</span>
                    </p>
                    <p className="truncate text-sm font-medium">{item.produtoDescricao}</p>
                    <p className="mt-0.5 text-xs text-textoFraco">
                      {item.resolvivel ? (
                        <>
                          {numero(item.disponivelNoCentro)} disp. em {numero(item.origensPossiveis)}{' '}
                          {plural(item.origensPossiveis, 'posição', 'posições')}
                        </>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-status-atencao">
                          <CircleSlash className="h-3 w-3" />
                          sem origem no centro ({numero(item.disponivelNoCentro)} de{' '}
                          {numero(item.faltante)})
                        </span>
                      )}
                    </p>
                  </div>
                  <span className="tabular shrink-0 rounded-lg bg-status-divergenciaFundo px-2 py-1 text-[13px] font-bold text-status-divergencia">
                    {numero(item.saldo)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Cartao>
  );
}

function PainelCompensacao({
  item,
  centro,
  podeMover,
}: {
  item: NegativoItem;
  centro: number;
  podeMover: boolean;
}): JSX.Element {
  const origens = useOrigens(centro, item.produto);
  const compensacao = useCompensacao();
  const [selecionadas, definirSelecionadas] = React.useState<number[]>([]);
  const [confirmando, definirConfirmando] = React.useState(false);
  const [fichaAberta, definirFichaAberta] = React.useState(false);
  const [arrastarAberto, definirArrastarAberto] = React.useState(false);

  const lista = React.useMemo(() => origens.data?.origens ?? [], [origens.data]);

  /*
   * A pré-seleção, em dois casos.
   *
   * O segundo caso existe porque a primeira versão só sabia resolver o
   * primeiro, e isso MATAVA a tela: quando nenhuma origem cobria o buraco
   * sozinha, nada era marcado, `pernas` ficava vazio e o botão principal
   * nascia desabilitado escrito "Compensar 0 un.". A tela listava as origens,
   * sugeria a compensação e não deixava executá-la.
   *
   * E não é raro: medido no centro 2201, em 9 dos 15 maiores negativos nenhuma
   * posição isolada cobre o faltante — o produto 33703 tem 255 origens de 4
   * unidades para um buraco de 142.
   */
  React.useEffect(() => {
    // 1. Uma origem basta: escolhemos a MENOR que cobre, porque esvaziar a
    //    posição pequena preserva os lotes grandes para o abastecimento normal.
    const suficientes = lista.filter((origem) => origem.saldo >= item.faltante);
    if (suficientes.length > 0) {
      const menor = suficientes.reduce<OrigemCandidata>(
        (melhor, origem) => (origem.saldo < melhor.saldo ? origem : melhor),
        suficientes[0] as OrigemCandidata,
      );
      definirSelecionadas([menor.localizador]);
      return;
    }

    // 2. Nenhuma cobre sozinha: soma da maior para a menor até fechar. A lista
    //    já vem ordenada por saldo decrescente da API, então isso também é o
    //    menor NÚMERO de transferências possível — cada perna é um movimento
    //    que alguém vai ter que fazer no chão.
    let restante = item.faltante;
    const escolhidas: number[] = [];
    for (const origem of lista) {
      if (restante <= 0) break;
      if (origem.saldo <= 0) continue;
      escolhidas.push(origem.localizador);
      restante -= origem.saldo;
    }
    definirSelecionadas(escolhidas);
  }, [lista, item.faltante]);

  // Aloca na ordem da API (maior saldo primeiro) até cobrir o faltante.
  const pernas = React.useMemo<Perna[]>(() => {
    let restante = item.faltante;
    const resultado: Perna[] = [];
    for (const origem of lista) {
      if (restante <= 0) break;
      if (!selecionadas.includes(origem.localizador)) continue;
      const quantidade = Math.min(origem.saldo, restante);
      if (quantidade <= 0) continue;
      resultado.push({
        origem: origem.localizador,
        origemEndereco: `${origem.corredor} ${origem.endereco}`.trim(),
        quantidade,
        estado: 'pendente',
      });
      restante -= quantidade;
    }
    return resultado;
  }, [lista, selecionadas, item.faltante]);

  const coberto = pernas.reduce((soma, perna) => soma + perna.quantidade, 0);
  const cobertura = item.faltante > 0 ? (coberto / item.faltante) * 100 : 0;
  const parcial = coberto > 0 && coberto < item.faltante;

  const alternar = (localizador: number): void =>
    definirSelecionadas((atual) =>
      atual.includes(localizador) ? atual.filter((l) => l !== localizador) : [...atual, localizador],
    );

  const aoSoltar = (evento: DragEndEvent): void => {
    if (evento.over?.id !== 'destino-negativo') return;
    const localizador = Number(evento.active.id);
    if (!Number.isFinite(localizador)) return;
    definirSelecionadas((atual) =>
      atual.includes(localizador) ? atual : [...atual, localizador],
    );
  };

  const executando = compensacao.executando;
  const pernasExecucao = compensacao.pernas;

  return (
    <DndContext onDragEnd={aoSoltar}>
      {/* Sticky: a lista de negativos é longa e, ao rolar até um item do fim,
          o painel (com o botão de transferir) ficava lá em cima. */}
      <Cartao className="flex max-h-[calc(100vh-120px)] flex-col overflow-hidden lg:sticky lg:top-4">
        <AreaDestino item={item} aoAbrirFicha={() => definirFichaAberta(true)} />

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="border-b border-borda px-4 py-3">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-textoFraco">
                Faltam <span className="tabular font-semibold text-texto">{numero(item.faltante)}</span> ·
                selecionado <span className="tabular font-semibold text-texto">{numero(coberto)}</span>
              </span>
              <span className="tabular text-sm font-semibold">{percentual(cobertura)}</span>
            </div>
            <div
              className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-superficie2"
              role="progressbar"
              aria-valuenow={Math.round(cobertura)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Cobertura do faltante"
            >
              <div
                className={cn('h-full rounded-full', coberto >= item.faltante ? 'bg-status-livre' : 'bg-primaria')}
                style={{ width: `${Math.min(100, cobertura)}%` }}
              />
            </div>
            {parcial ? (
              <p className="mt-2 text-xs text-status-atencao">
                Cobertura parcial: a posição continuará negativa em{' '}
                {numero(item.faltante - coberto)} un. Ainda assim é melhor que não fazer nada.
              </p>
            ) : null}
          </div>

          <div className="px-4 py-3">
            {/* O que faltava ficar claro: isto é palpite, não determinação. */}
            <p className="mb-2 flex items-start gap-2 rounded-lg border border-primaria/20 bg-primaria-suave px-3 py-2 text-[11.5px] leading-relaxed">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primaria" />
              <span>
                <b>Sugestão do sistema</b>, não determinação: ele lista as posições que têm este SKU
                e marca a menor que cobre o buraco sozinha. Ordena por saldo e{' '}
                <b>não sabe qual fica mais perto</b>. Para escolher outra, use o botão ao lado —
                por código, por endereço ou arrastando no desenho da rua.
              </span>
            </p>

            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className="rotulo">Origens sugeridas</h3>
              <Botao
                variante="secundaria"
                tamanho="pequeno"
                onClick={() => definirArrastarAberto(true)}
              >
                <Hand className="h-3.5 w-3.5" />
                Escolher outra / arrastar
              </Botao>
            </div>

            {origens.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }, (_, i) => (
                  <Esqueleto key={i} className="h-11" />
                ))}
              </div>
            ) : origens.isError ? (
              <EstadoErro erro={origens.error} aoTentarDeNovo={() => void origens.refetch()} />
            ) : lista.length === 0 ? (
              <EstadoVazio
                titulo="Sem origem no centro"
                detalhe="Nenhuma posição deste centro tem esse SKU. Resolver exige inventário ou transferência de outro CD — decisão humana, não automática."
                icone={<CircleSlash className="h-8 w-8" />}
              />
            ) : (
              <ul className="space-y-1.5">
                {lista.map((origem) => (
                  <LinhaOrigem
                    key={origem.localizador}
                    origem={origem}
                    sku={item.produto}
                    marcada={selecionadas.includes(origem.localizador)}
                    quantidade={pernas.find((p) => p.origem === origem.localizador)?.quantidade ?? 0}
                    aoAlternar={() => alternar(origem.localizador)}
                    desabilitada={!podeMover || executando}
                  />
                ))}
              </ul>
            )}
          </div>

        </div>

        {/*
          O RESULTADO FICA COLADO NO BOTÃO, fora da área que rola.

          Antes ele ficava no fim da lista de origens, dentro do scroll — e numa
          compensação de muitas pernas a lista tem dezenas de linhas. O usuário
          clicava, a API respondia com a recusa do PBS, e a mensagem era pintada
          a centenas de pixels abaixo do que estava à vista: de fora, o botão
          simplesmente não fazia nada. Feedback que não está no campo de visão
          de quem agiu não é feedback.
        */}
        {pernasExecucao.length > 0 ? (
          <div className="shrink-0 border-t border-borda px-4 py-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-textoFraco">
              Execução
            </h3>
            {/* Teto próprio: 36 pernas não podem empurrar o botão para fora. */}
            <ul className="max-h-32 space-y-1 overflow-y-auto">
              {pernasExecucao.map((perna) => (
                <li key={perna.origem} className="flex items-center gap-2 text-sm">
                  <EstadoIcone estado={perna.estado} />
                  <span className="font-mono text-xs">{perna.origemEndereco}</span>
                  <span className="tabular ml-auto font-medium">{numero(perna.quantidade)}</span>
                </li>
              ))}
            </ul>
            {compensacao.erroGeral ? (
              <p className="mt-2 rounded border border-status-divergencia/40 bg-status-divergenciaFundo px-3 py-2 text-xs text-status-divergencia">
                {compensacao.escritaDesabilitada
                  ? 'Movimentação desabilitada nesta instalação (WMS_ALLOW_WRITES=false). Nada foi gravado nesta perna.'
                  : compensacao.erroGeral}
                {compensacao.gravouAlgo
                  ? ' As transferências marcadas como concluídas acima JÁ foram gravadas e não são desfeitas.'
                  : ''}
              </p>
            ) : null}
            {!compensacao.erroGeral && !executando && pernasExecucao.every((p) => p.estado === 'ok') ? (
              <p className="mt-2 rounded border border-status-livre/40 bg-status-livreFundo px-3 py-2 text-xs text-status-livre">
                Compensação concluída: {numero(pernasExecucao.length)}{' '}
                {plural(pernasExecucao.length, 'transferência', 'transferências')} gravadas.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="shrink-0 border-t border-borda p-3">
          <Botao
            variante="primaria"
            className="w-full"
            disabled={!podeMover || executando || pernas.length === 0}
            onClick={() => definirConfirmando(true)}
          >
            {executando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : !podeMover ? (
              <Lock className="h-4 w-4" />
            ) : null}
            {/*
              Botão desabilitado precisa DIZER o porquê. "Compensar 0 un." era
              o texto quando não havia nada marcado, e não explicava nada — de
              fora parece um botão que não funciona.
            */}
            {executando
              ? 'Compensando…'
              : !podeMover
                ? 'Sem permissão para movimentar'
                : lista.length === 0
                  ? 'Nenhuma posição tem este SKU'
                  : pernas.length === 0
                    ? 'Marque ao menos uma origem'
                    : `Compensar ${numero(coberto)} un.`}
          </Botao>
        </div>
      </Cartao>

      <DrawerProduto sku={fichaAberta ? item.produto : null} aoFechar={() => definirFichaAberta(false)} />

      {/* Caminho alternativo à sugestão: escolher a origem por código, por
          endereço, ou arrastando no desenho da rua. */}
      <TransferenciaArrastar
        aberto={arrastarAberto}
        centro={centro}
        produto={item.produto}
        descricaoProduto={item.produtoDescricao}
        destino={{
          localizador: item.localizador,
          endereco: item.endereco,
          saldo: item.saldo,
        }}
        faltante={item.faltante}
        aoFechar={() => definirArrastarAberto(false)}
        aoConcluir={() => definirArrastarAberto(false)}
      />

      <DialogoConfirmacao
        aberto={confirmando}
        item={item}
        pernas={pernas}
        coberto={coberto}
        aoCancelar={() => definirConfirmando(false)}
        aoConfirmar={() => {
          definirConfirmando(false);
          void compensacao.executar(centro, item.produto, item.localizador, pernas);
        }}
      />
    </DndContext>
  );
}

/** Área de soltura do arraste — e cabeçalho do destino. */
function AreaDestino({
  item,
  aoAbrirFicha,
}: {
  item: NegativoItem;
  aoAbrirFicha: () => void;
}): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: 'destino-negativo' });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'border-b border-borda px-4 py-3 transition-colors',
        isOver && 'bg-primaria-suave',
      )}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={aoAbrirFicha}
          aria-label={`Ver ficha do produto ${item.produto}`}
        >
          <ImagemProduto sku={item.produto} tamanho={56} alt={item.produtoDescricao} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-mono text-sm font-semibold">SKU {item.produto}</p>
            <button
              type="button"
              onClick={aoAbrirFicha}
              className="rounded text-xs text-textoFraco underline-offset-2 hover:underline"
            >
              ficha
            </button>
          </div>
          <p className="truncate text-sm text-textoFraco">{item.produtoDescricao}</p>
          <p className="mt-1 flex items-center gap-1 font-mono text-xs text-textoFraco">
            destino
            <ArrowRight className="h-3 w-3" />
            {item.corredor} {item.endereco}
            <span className="tabular ml-1 font-semibold text-status-divergencia">
              {numero(item.saldo)}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

function LinhaOrigem({
  origem,
  sku,
  marcada,
  quantidade,
  aoAlternar,
  desabilitada,
}: {
  origem: OrigemCandidata;
  sku: number;
  marcada: boolean;
  quantidade: number;
  aoAlternar: () => void;
  desabilitada: boolean;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: origem.localizador,
    disabled: desabilitada,
  });

  return (
    <li
      ref={setNodeRef}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      className={cn(
        'flex items-center gap-2 rounded border border-borda px-2 py-1.5',
        marcada ? 'border-primaria bg-primaria-suave' : 'bg-superficie',
        isDragging && 'opacity-70 shadow-lg',
      )}
    >
      {/* Arrastar é atalho; o checkbox é o caminho que funciona no coletor. */}
      <label className="flex min-h-toque flex-1 cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          className="h-4 w-4 accent-[rgb(var(--cor-primaria))]"
          checked={marcada}
          onChange={aoAlternar}
          disabled={desabilitada}
        />
        <ImagemProduto sku={sku} tamanho={28} alt="" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-xs">
            {origem.corredor} {origem.endereco}
          </span>
          <span className="block text-xs text-textoFraco">
            saldo {numero(origem.saldo)}
            {marcada && quantidade > 0 ? ` · levar ${numero(quantidade)}` : ''}
          </span>
        </span>
      </label>
      <button
        type="button"
        aria-label={`Arrastar origem ${origem.endereco}`}
        className="flex h-9 w-9 cursor-grab items-center justify-center rounded text-textoFraco hover:bg-superficie2"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
    </li>
  );
}

function EstadoIcone({ estado }: { estado: Perna['estado'] }): JSX.Element {
  if (estado === 'ok') return <Check className="h-4 w-4 shrink-0 text-status-livre" />;
  if (estado === 'erro') return <X className="h-4 w-4 shrink-0 text-status-divergencia" />;
  if (estado === 'executando') return <Loader2 className="h-4 w-4 shrink-0 animate-spin" />;
  if (estado === 'cancelada') return <CircleSlash className="h-4 w-4 shrink-0 text-textoFraco" />;
  return <span className="h-4 w-4 shrink-0 rounded-full border border-borda" />;
}

/** Confirmação explícita antes de gravar: origem, destino, SKU, quantidade,
 *  saldo depois e os alertas. Sem isso, movimentação vira acidente. */
function DialogoConfirmacao({
  aberto,
  item,
  pernas,
  coberto,
  aoCancelar,
  aoConfirmar,
}: {
  aberto: boolean;
  item: NegativoItem;
  pernas: readonly Perna[];
  coberto: number;
  aoCancelar: () => void;
  aoConfirmar: () => void;
}): JSX.Element {
  const saldoApos = item.saldo + coberto;
  return (
    <Dialog.Root open={aberto} onOpenChange={(v) => !v && aoCancelar()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        {/*
          Altura LIMITADA e conteúdo rolável, e não é detalhe de acabamento.

          O diálogo lista uma linha por perna. Sem teto de altura, uma
          compensação de muitas origens — 36 pernas num caso real do centro
          2201 — crescia além da tela e, como ele é centrado por
          `-translate-y-1/2`, era cortado EM CIMA E EMBAIXO ao mesmo tempo. O
          botão "Confirmar" ficava fora da tela sem nenhuma forma de rolar até
          ele: o diálogo simplesmente não podia ser confirmado.

          Agora o miolo rola e título e botões ficam sempre visíveis.
        */}
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[min(90vh,720px)] w-[min(520px,94vw)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-borda bg-superficie p-4 shadow-2xl">
          <Dialog.Title className="shrink-0 text-base font-semibold">
            Confirmar compensação
          </Dialog.Title>
          <Dialog.Description className="mt-1 shrink-0 text-sm text-textoFraco">
            SKU {item.produto} · {item.produtoDescricao}
          </Dialog.Description>

          <ul className="mt-3 min-h-0 flex-1 divide-y divide-borda overflow-y-auto rounded border border-borda">
            {pernas.map((perna) => (
              <li key={perna.origem} className="flex items-center gap-2 px-3 py-2 text-sm">
                <span className="font-mono text-xs">{perna.origemEndereco}</span>
                <ArrowRight className="h-3 w-3 shrink-0 text-textoFraco" />
                <span className="font-mono text-xs">
                  {item.corredor} {item.endereco}
                </span>
                <span className="tabular ml-auto font-semibold">{numero(perna.quantidade)}</span>
              </li>
            ))}
          </ul>

          <dl className="mt-3 shrink-0 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
            <dt className="text-textoFraco">Saldo atual do destino</dt>
            <dd className="tabular text-right font-medium text-status-divergencia">
              {numero(item.saldo)}
            </dd>
            <dt className="text-textoFraco">Saldo após</dt>
            <dd
              className={cn(
                'tabular text-right font-medium',
                saldoApos < 0 ? 'text-status-atencao' : 'text-status-livre',
              )}
            >
              {numero(saldoApos)}
            </dd>
          </dl>

          <div className="mt-3 shrink-0 space-y-2 text-xs">
            {pernas.length > 1 ? (
              <p className="flex items-start gap-2 rounded border border-status-atencao/40 bg-status-atencaoFundo px-3 py-2 text-status-atencao">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                São {pernas.length} transferências independentes. Se uma falhar no meio, as
                anteriores já terão sido gravadas e não são desfeitas.
              </p>
            ) : null}
            {saldoApos < 0 ? (
              <p className="text-status-atencao">
                A posição continuará negativa em {numero(Math.abs(saldoApos))} un.
              </p>
            ) : null}
          </div>

          <div className="mt-4 shrink-0 flex justify-end gap-2">
            <Botao variante="secundaria" tamanho="pequeno" onClick={aoCancelar}>
              Cancelar
            </Botao>
            <Botao variante="primaria" tamanho="pequeno" onClick={aoConfirmar}>
              Confirmar {numero(coberto)} un.
            </Botao>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
